import { useEffect, useMemo, useState } from 'react'
import { Link as RouterLink, useNavigate } from 'react-router-dom'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Link,
  Stack,
  Typography,
} from '@mui/material'
import {
  IconCalendarEvent,
  IconSpeakerphone,
  IconSquarePlus,
} from '@tabler/icons-react'
import { tokens } from '@/theme'
import {
  daysUntil,
  listAdminAnnouncements,
  listAdminMeetings,
  TenantAdminApiError,
  type AdminAnnouncement,
  type AdminMeeting,
} from '@/lib/tenantAdminApi'
import { formatDay } from '@/lib/dateOnly'
import { useTenantAdmin } from '@/pages/tenant-admin/context'

/**
 * Screen 2 — admin dashboard: what's published, drafts pending, and the next
 * meeting with its Brown-Act posting-deadline indicator (ag-008 wireframes).
 */
export default function TenantAdminDashboard() {
  const { slug, displayName } = useTenantAdmin()
  const navigate = useNavigate()
  const [meetings, setMeetings] = useState<AdminMeeting[]>([])
  const [announcements, setAnnouncements] = useState<AdminAnnouncement[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    Promise.all([listAdminMeetings(slug), listAdminAnnouncements(slug)])
      .then(([ms, as]) => {
        if (cancelled) return
        setMeetings(ms)
        setAnnouncements(as)
      })
      .catch((err) => {
        if (cancelled) return
        if (err instanceof TenantAdminApiError && err.status === 401) {
          navigate(`/g/${slug}/admin/login`, { replace: true })
          return
        }
        setError(err instanceof Error ? err.message : 'Failed to load your dashboard.')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [slug, navigate])

  const publishedCount = announcements.filter((a) => a.status === 'published').length
  const draftCount = announcements.filter((a) => a.status === 'draft').length

  // Next upcoming meeting = smallest meeting_date that is today or later.
  const nextMeeting = useMemo(() => {
    const upcoming = meetings
      .filter((m) => {
        const days = daysUntil(m.meeting_date)
        return days !== null && days >= 0
      })
      .sort((a, b) => (a.meeting_date ?? '').localeCompare(b.meeting_date ?? ''))
    return upcoming[0] ?? null
  }, [meetings])

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 12 }}>
        <CircularProgress size={32} sx={{ color: tokens.green.main }} />
      </Box>
    )
  }

  return (
    <Box>
      {error ? (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      ) : null}

      {/* ── Primary actions ─────────────────────────────────────────────── */}
      <Stack direction="row" spacing={1.5} useFlexGap sx={{ flexWrap: 'wrap', mb: 4 }}>
        <Button
          component={RouterLink}
          to={`/g/${slug}/admin/meetings/new`}
          variant="contained"
          startIcon={<IconSquarePlus size={18} />}
        >
          Post a meeting
        </Button>
        <Button
          component={RouterLink}
          to={`/g/${slug}/admin/announcements`}
          variant="outlined"
          startIcon={<IconSpeakerphone size={18} />}
        >
          New announcement
        </Button>
      </Stack>

      {/* ── Stat cards ──────────────────────────────────────────────────── */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
          gap: 2.5,
          mb: 5,
        }}
      >
        <StatCard
          label="Published announcements"
          value={String(publishedCount)}
          hint="Live on your public page"
        />
        <StatCard
          label="Drafts"
          value={String(draftCount)}
          hint={draftCount > 0 ? 'Waiting for you to publish' : 'Nothing waiting'}
        />
        <NextMeetingCard meeting={nextMeeting} />
      </Box>

      {/* ── Recent activity ─────────────────────────────────────────────── */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: 3,
        }}
      >
        <Card>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Recent meetings
            </Typography>
            {meetings.length === 0 ? (
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                No meetings posted yet.{' '}
                <Link component={RouterLink} to={`/g/${slug}/admin/meetings/new`}>
                  Post your first meeting
                </Link>
                .
              </Typography>
            ) : (
              <Stack divider={<Divider />} spacing={1.5}>
                {meetings.slice(0, 5).map((m) => (
                  <Stack
                    key={m.meeting_id}
                    direction="row"
                    spacing={1}
                    sx={{ alignItems: 'center', justifyContent: 'space-between', pt: 0.5 }}
                  >
                    <Stack direction="row" spacing={1} sx={{ alignItems: 'center', minWidth: 0 }}>
                      <IconCalendarEvent size={16} color={tokens.green.main} />
                      <Typography variant="body2" noWrap>
                        {formatDay(m.meeting_date) || 'Undated'}
                        {m.meeting_type ? ` · ${m.meeting_type}` : ''}
                      </Typography>
                    </Stack>
                    <Chip
                      label={`Posted by ${displayName}`}
                      size="small"
                      variant="outlined"
                      color="primary"
                    />
                  </Stack>
                ))}
              </Stack>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Announcements
            </Typography>
            {announcements.length === 0 ? (
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                No announcements yet.{' '}
                <Link component={RouterLink} to={`/g/${slug}/admin/announcements`}>
                  Write one
                </Link>
                .
              </Typography>
            ) : (
              <Stack divider={<Divider />} spacing={1.5}>
                {announcements.slice(0, 5).map((a) => (
                  <Stack
                    key={a.id}
                    direction="row"
                    spacing={1}
                    sx={{ alignItems: 'center', justifyContent: 'space-between', pt: 0.5 }}
                  >
                    <Typography variant="body2" noWrap sx={{ minWidth: 0 }}>
                      {a.title}
                    </Typography>
                    <StatusChip status={a.status} />
                  </Stack>
                ))}
              </Stack>
            )}
          </CardContent>
        </Card>
      </Box>
    </Box>
  )
}

function StatCard({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <Card>
      <CardContent sx={{ p: 3 }}>
        <Typography variant="overline" sx={{ color: 'text.secondary', display: 'block' }}>
          {label}
        </Typography>
        <Typography variant="h3" sx={{ color: tokens.green.main, my: 0.5 }}>
          {value}
        </Typography>
        {hint ? (
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {hint}
          </Typography>
        ) : null}
      </CardContent>
    </Card>
  )
}

/**
 * "Next meeting" stat card with the Brown-Act posting-deadline indicator:
 * green when the agenda window is satisfied (or no statutory minimum), amber
 * when inside the window without a posted agenda.
 */
function NextMeetingCard({ meeting }: { meeting: AdminMeeting | null }) {
  if (!meeting) {
    return <StatCard label="Next meeting" value="—" hint="No upcoming meeting posted" />
  }
  const days = daysUntil(meeting.meeting_date)
  const countdown = days === null ? '—' : days === 0 ? 'Today' : `${days}d`
  const agendaPosted = Boolean(meeting.agenda_url)
  const windowOk = meeting.brown_act.ok

  let chip: { label: string; color: 'success' | 'warning' | 'error' }
  if (agendaPosted) {
    chip = { label: 'Agenda posted', color: 'success' }
  } else if (windowOk) {
    chip = { label: 'Agenda not posted yet', color: 'warning' }
  } else {
    chip = { label: 'Agenda posting window passed', color: 'error' }
  }

  return (
    <Card>
      <CardContent sx={{ p: 3 }}>
        <Typography variant="overline" sx={{ color: 'text.secondary', display: 'block' }}>
          Next meeting
        </Typography>
        <Typography variant="h3" sx={{ color: tokens.green.main, my: 0.5 }}>
          {countdown}
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1 }}>
          {formatDay(meeting.meeting_date)}
          {meeting.meeting_type ? ` · ${meeting.meeting_type}` : ''}
        </Typography>
        <Chip label={chip.label} size="small" color={chip.color} />
        {!windowOk && meeting.brown_act.note ? (
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 1 }}>
            {meeting.brown_act.note}
          </Typography>
        ) : null}
      </CardContent>
    </Card>
  )
}

export function StatusChip({ status }: { status: AdminAnnouncement['status'] }) {
  const map: Record<AdminAnnouncement['status'], { label: string; color: 'success' | 'default' | 'warning' }> = {
    published: { label: 'Published', color: 'success' },
    draft: { label: 'Draft', color: 'warning' },
    archived: { label: 'Archived', color: 'default' },
  }
  const cfg = map[status] ?? { label: status, color: 'default' as const }
  return <Chip label={cfg.label} size="small" color={cfg.color} variant="outlined" />
}
